/**
 * Unified SiteTheme API
 * Provides a simple interface for theme management across the application
 * Only manages data-theme attribute - CSS variables are handled by CSS files
 * Integrates with LocalUserPreferences for user theme selection persistence
 */

import { SaveCMSContent } from "aldoo-ra/CMS/api"
import { GetCMSContent } from "aldoo-ra/CMS/api"
import { api } from "aldoo-ra/API"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"

const siteThemeAPI = api("site-theme")

let currentTheme = null
let isInitialized = false
let initializationPromise = null
let setThemePromises = new Map() // Track ongoing setTheme calls
let onThemeChangeCallbacks = []

// Simple request cache to prevent duplicate API calls
const apiCache = new Map()
const CACHE_DURATION = 5000 // 5 seconds

// Helper function to create cache key for API requests
const createCacheKey = (functionName, params) => {
  return `${functionName}:${JSON.stringify(params)}`
}

// Helper function to get cached result or execute function
const withCache = async (cacheKey, asyncFunction) => {
  const now = Date.now()

  // Check if we have a cached result that's still valid
  const cached = apiCache.get(cacheKey)
  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    return cached.result
  }

  // Execute the function and cache the result
  const result = await asyncFunction()
  apiCache.set(cacheKey, {
    result,
    timestamp: now
  })

  return result
}

// Helper function to normalize isActive field to array format
const normalizeIsActive = (isActive) => {
  if (Array.isArray(isActive)) return isActive
  return [] // Any other value = inactive
}

// LocalUserPreferences keys for storing user's selected themes
const USER_SITE_THEME_PREFERENCE_KEY = "ui.theme.site.selectedId"
const USER_ADMIN_THEME_PREFERENCE_KEY = "ui.theme.admin.selectedId"

/**
 * Get the appropriate context (admin or site) based on current URL
 * Force 'site' context for theme previews
 */
const getCurrentContext = () => {
  if (typeof window === 'undefined') return 'site'

  // Force site context for theme previews regardless of URL
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('theme_preview')) {
    return 'site'
  }

  return window.location.pathname.startsWith('/admin') ? 'admin' : 'site'
}

/**
 * Get user preference key for current context
 */
const getUserPreferenceKeyForContext = (context = null) => {
  const currentContext = context || getCurrentContext()
  return currentContext === 'admin' ? USER_ADMIN_THEME_PREFERENCE_KEY : USER_SITE_THEME_PREFERENCE_KEY
}

// Global flag to prevent multiple URL change listeners
let urlChangeListenerSetup = false

/**
 * Set up URL change listener to handle theme switching on navigation
 */
const setupUrlChangeListener = () => {
  if (typeof window === 'undefined' || urlChangeListenerSetup) return

  let currentContext = getCurrentContext()
  let urlChangeTimeout = null
  let isThemeSwitching = false

  const handleContextChange = async () => {
    if (isThemeSwitching) return

    const newContext = getCurrentContext()

    if (newContext !== currentContext) {
      isThemeSwitching = true
      try {
        currentContext = newContext

        // Get user's preferred theme for the new context
        const contextPreferenceKey = getUserPreferenceKeyForContext(newContext)
        let userPreferredThemeId = LocalUserPreferences.get(contextPreferenceKey)

        let newTheme = null

        if (userPreferredThemeId) {
          // Try to get user's preferred theme
          try {
            const candidateTheme = await getThemeByIdFromAPI(userPreferredThemeId)
            if (candidateTheme) {
              // User preferences override context validation - if user explicitly chose this theme, apply it
              newTheme = candidateTheme
            } else {
              console.warn('User preferred theme with ID not found:', userPreferredThemeId)
            }
          } catch (error) {
            console.warn('Failed to load user preferred theme:', error)
          }
        }

        if (!newTheme) {
          // Fall back to active theme for the context
          try {
            newTheme = await getActiveThemeForContextFromAPI(newContext)
          } catch (error) {
            console.warn('Failed to load active theme for context:', error)
          }
        }

        if (!newTheme) {
          // Final fallback to default theme
          try {
            newTheme = await getDefaultThemeFromAPI()
          } catch (error) {
            console.warn('Failed to load default theme:', error)
          }
        }

        if (newTheme) {
          currentTheme = newTheme
          await applyThemeToDOM(newTheme, newContext)

          // Force re-apply after a delay to ensure theme sticks during browser navigation
          setTimeout(async () => {
            await applyThemeToDOM(newTheme, newContext)
          }, 100)

          notifyThemeChange(newTheme)
        } else {
          console.warn('No theme found for context:', newContext)
        }
      } finally {
        isThemeSwitching = false
      }
    }
  }

  // Listen for URL changes (works with client-side routing)
  const originalPushState = window.history.pushState
  const originalReplaceState = window.history.replaceState

  window.history.pushState = function(...args) {
    originalPushState.apply(window.history, args)
    clearTimeout(urlChangeTimeout)
    urlChangeTimeout = setTimeout(handleContextChange, 10)
  }

  window.history.replaceState = function(...args) {
    originalReplaceState.apply(window.history, args)
    clearTimeout(urlChangeTimeout)
    urlChangeTimeout = setTimeout(handleContextChange, 10)
  }

  // Also listen for popstate (back/forward buttons) with longer delay
  window.addEventListener('popstate', () => {
    clearTimeout(urlChangeTimeout)
    urlChangeTimeout = setTimeout(handleContextChange, 50)  // Longer delay for browser navigation
  })

  urlChangeListenerSetup = true
}

/**
 * Initialize the theme system
 * Should be called once during app startup
 * Priority: User Preference (context-specific) > Active Theme > Default Theme
 */
const initialize = async (initialData = null) => {
  if (isInitialized) return currentTheme

  // If initialization is already in progress, wait for it to complete
  if (initializationPromise) {
    return await initializationPromise
  }

  // Mark initialization as in progress and create the promise
  initializationPromise = performInitialization(initialData)

  try {
    return await initializationPromise
  } catch (error) {
    // Reset promise on error so initialization can be retried
    initializationPromise = null
    throw error
  }
}

/**
 * Internal initialization function that does the actual work
 */
const performInitialization = async (initialData = null) => {
  try {
    let selectedTheme = null
    const currentContext = getCurrentContext()

    // Use initialData or global window data from SSR script
    const ssrThemeData = initialData || (typeof window !== 'undefined' ? window.__INITIAL_THEME_DATA__ : null)

    // 0. Check for theme preview override first (highest priority)
    let themePreviewId = null
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      themePreviewId = urlParams.get('theme_preview')
    }

    // If we have a theme preview, use it directly and skip user preferences
    if (themePreviewId) {
      // For theme previews, always use the full applyThemeToDOM flow
      // This ensures proper CSS variable clearing and CMS color generation
      if (ssrThemeData?.availableThemes) {
        selectedTheme = ssrThemeData.availableThemes.find(theme => theme._id === themePreviewId)
      }

      if (!selectedTheme) {
        selectedTheme = await getThemeByIdFromAPI(themePreviewId)
      }

      if (selectedTheme) {
        currentTheme = selectedTheme
        await applyThemeToDOM(selectedTheme, 'site') // Always use site context for previews
        isInitialized = true
        setupUrlChangeListener()
        return currentTheme
      }
    }

    // 1. Check if user has a saved theme preference for current context
    const contextPreferenceKey = getUserPreferenceKeyForContext(currentContext)
    let userPreferredThemeId = LocalUserPreferences.get(contextPreferenceKey)

    if (userPreferredThemeId) {
      if (ssrThemeData?.availableThemes) {
        // Try to find user preferred theme in SSR data first (faster)
        selectedTheme = ssrThemeData.availableThemes.find(theme => theme._id === userPreferredThemeId)
        if (!selectedTheme) {
          // Fall back to API if not in SSR data
          selectedTheme = await getThemeByIdFromAPI(userPreferredThemeId)
        }
      } else {
        // No SSR data, try API
        selectedTheme = await getThemeByIdFromAPI(userPreferredThemeId)
      }
    }

    // 2. If no user preference, use context-specific active theme from SSR
    if (!selectedTheme && ssrThemeData?.activeTheme) {
      // Check if the active theme is active for the current context
      const activeTheme = ssrThemeData.activeTheme
      const isActiveForContext = normalizeIsActive(activeTheme.isActive).includes(currentContext)

      if (isActiveForContext) {
        selectedTheme = activeTheme
      }
    }

    // 3. Fall back to backend API to find context-specific active theme
    if (!selectedTheme && !ssrThemeData) {
      selectedTheme = await getActiveThemeForContextFromAPI(currentContext)
    }

    // 4. Final fallback to default theme
    if (!selectedTheme && !ssrThemeData) {
      selectedTheme = await getDefaultThemeFromAPI()
    }

    if (selectedTheme) {
      currentTheme = selectedTheme
      await applyThemeToDOM(selectedTheme, currentContext)
    }

    isInitialized = true

    // Set up URL change listener for dynamic theme switching
    setupUrlChangeListener()
    return currentTheme
  } catch (error) {
    return null
  } finally {
    isInitialized = true
    initializationPromise = null
  }
}

/**
 * Get current active theme
 * @returns {Object|null} Current theme object or null
 */
const getTheme = () => {
  return currentTheme
}

/**
 * Get user's preferred theme ID from LocalUserPreferences
 * @param {string} context - Context ('admin' or 'site'), defaults to current context
 * @returns {string|null} Theme ID or null if no preference set
 */
const getUserPreferredThemeId = (context = null) => {
  const currentContext = context || getCurrentContext()
  const preferenceKey = getUserPreferenceKeyForContext(currentContext)
  let themeId = LocalUserPreferences.get(preferenceKey)


  return themeId
}

/**
 * Clear user's theme preference (will fall back to site default)
 * @param {string} context - Context ('admin' or 'site'), defaults to current context
 */
const clearUserPreference = (context = null) => {
  const currentContext = context || getCurrentContext()
  const preferenceKey = getUserPreferenceKeyForContext(currentContext)
  LocalUserPreferences.delete(preferenceKey)

}

/**
 * Set site-wide active theme (admin function)
 * This sets the global active theme and does NOT save as user preference
 * @param {string} themeId - Theme ID to set as site-wide active
 * @returns {Promise<Object>} Applied theme object
 */
const setSiteActiveTheme = async (themeId) => {
  try {
    if (!themeId) {
      throw new Error('Theme ID is required')
    }

    // Get the theme from backend
    const theme = await getThemeByIdFromAPI(themeId)

    if (!theme) {
      throw new Error(`Theme with ID ${themeId} not found`)
    }

    // First deactivate all themes
    await deactivateAllThemes()

    // Then activate the selected theme
    await activateThemeInAPI(themeId)

    // Apply to DOM and update current theme (but don't save as user preference)
    currentTheme = theme
    await applyThemeToDOM(theme, 'site')  // Site-wide themes are applied to site context

    // Notify listeners
    notifyThemeChange(theme)


    return theme
  } catch (error) {
    console.error('Failed to set site active theme:', error)
    throw error
  }
}

/**
 * Set theme by ID
 * @param {string} themeId - Theme ID to activate
 * @param {boolean} applyImmediately - Whether to apply the theme immediately (default: true)
 * @param {string} preferenceKey - Custom preference key (optional, for admin interface use)
 * @returns {Promise<Object>} Theme object
 */
const setTheme = async (themeId, applyImmediately = true, preferenceKey = null) => {
  const promiseKey = `${themeId}:${applyImmediately}:${preferenceKey || 'default'}`

  // If same setTheme call is already in progress, wait for it
  if (setThemePromises.has(promiseKey)) {
    return await setThemePromises.get(promiseKey)
  }

  // Create the promise and store it
  const themePromise = performSetTheme(themeId, applyImmediately, preferenceKey)
  setThemePromises.set(promiseKey, themePromise)

  try {
    const result = await themePromise
    return result
  } finally {
    setThemePromises.delete(promiseKey)
  }
}

/**
 * Internal setTheme function that does the actual work
 */
const performSetTheme = async (themeId, applyImmediately = true, preferenceKey = null) => {
  try {
    if (!themeId) {
      throw new Error('Theme ID is required')
    }

    // Get the theme from backend
    const theme = await getThemeByIdFromAPI(themeId)

    if (!theme) {
      throw new Error(`Theme with ID ${themeId} not found`)
    }

    // Always save user preference when setting a theme
    const keyToUse = preferenceKey || getUserPreferenceKeyForContext()
    LocalUserPreferences.set(keyToUse, themeId)

    // Only apply to DOM and update current theme if requested
    if (applyImmediately) {
      currentTheme = theme
      await applyThemeToDOM(theme)  // Uses current context from getCurrentContext()

      // Notify listeners
      notifyThemeChange(theme)
    }

    // Note: We don't activate in backend API since this is user preference, not site-wide setting
    // Admin users can still set site-wide active themes through the admin interface

    return theme
  } catch (error) {
    console.error('Failed to set theme:', error)
    throw error
  }
}

/**
 * Apply theme to DOM - sets CSS variables and data-theme attribute
 * CSS variables are dynamically applied from the theme's colors object
 * Context-aware theme application (admin vs site)
 */
const applyThemeToDOM = async (theme, context = null) => {
  if (typeof window === 'undefined') return

  const root = document.documentElement
  const currentContext = context || getCurrentContext()

  // Helper function to clear all theme CSS variables
  const clearAllThemeCSSVariables = () => {
    const cssVariablesToRemove = []

    // Collect all --color- CSS variables (both admin and site)
    for (let i = 0; i < root.style.length; i++) {
      const property = root.style.item(i)
      if (property.startsWith('--color-')) {
        cssVariablesToRemove.push(property)
      }
    }

    // Remove all collected CSS variables
    cssVariablesToRemove.forEach(property => {
      root.style.removeProperty(property)
    })

    return cssVariablesToRemove.length
  }

  // Always clear all existing theme CSS variables first
  clearAllThemeCSSVariables()

  // Handle built-in theme - just remove data-theme to use default CSS
  if (theme?.builtIn === true || theme?.name === 'Built-in') {
    // Remove data-theme attribute to use default CSS
    root.removeAttribute('data-theme')

    // Clear any existing CMS color utilities since built-in theme uses default CSS
    const cssElement = document.getElementById('cms-color-utilities')
    if (cssElement) {
      cssElement.innerHTML = ''
    }

    // Store built-in theme metadata
    root.setAttribute('data-theme-id', theme._id || theme.id || 'built-in')
    root.setAttribute('data-theme-name', 'Built-in')
    root.setAttribute('data-theme-version', theme.version || '1.0.0')

    // Dispatch custom event
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('themeChanged', {
        detail: { theme, themeIdentifier: 'built-in', context: currentContext }
      }))
    }

    return
  }

  // Handle regular themes
  if (!theme) return

  // Generate theme identifier from name
  const themeIdentifier = generateThemeIdentifier(theme.name)

  // Store theme metadata for debugging and introspection
  root.setAttribute('data-theme-id', theme._id || theme.id || 'unknown')
  root.setAttribute('data-theme-name', theme.name || 'Unknown')
  root.setAttribute('data-theme-version', theme.version || '1.0.0')
  root.setAttribute('data-theme-context', currentContext)

  // Save theme ID to cookie for SSR optimization
  if (typeof document !== 'undefined') {
    const themeId = theme._id || theme.id
    if (themeId) {
      document.cookie = `themeId_${currentContext}=${themeId}; path=/; max-age=31536000`
    }
  }

  // Default theme relies on Layout.css :root variables as the source of truth.
  // Applying its colors (stored as top-level props in old schema) as inline styles
  // would override Layout.css changes. Skip color application + CMS utilities.
  if (theme.isDefault) {
    root.setAttribute('data-theme', themeIdentifier)

    // Clear any CMS color utilities so Layout.css variables take over
    const cssElement = document.getElementById('cms-color-utilities')
    if (cssElement) cssElement.innerHTML = ''

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('themeChanged', {
        detail: { theme, themeIdentifier, context: currentContext }
      }))
    }

    return
  }

  // Set data-theme attribute for CSS selectors that still need it
  root.setAttribute('data-theme', themeIdentifier)

  // Apply CSS variables from theme colors object
  if (theme.colors) {
    let adminKeysCount = 0;

    const applyColor = (key, value) => {
        if (!key || !value) return;
        const cssVarName = `--color-${key.replace(/_/g, '_')}`;
        if (key.startsWith('admin_')) adminKeysCount++;
        root.style.setProperty(cssVarName, value);
    };

    if (Array.isArray(theme.colors)) {
        theme.colors.forEach(color => applyColor(color.key, color.value));
    } else if (typeof theme.colors === 'object') {
        Object.entries(theme.colors).forEach(([key, value]) => applyColor(key, value));
    }
  }

  // Generate and inject CMS color utilities for this theme
  await generateCMSColorUtilities(theme)

  // Dispatch custom event for any components that need to react to theme changes
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('themeChanged', {
      detail: { theme, themeIdentifier, context: currentContext }
    }))
  }
}

/**
 * Generate consistent theme identifier for data-theme attribute
 */
const generateThemeIdentifier = (themeName) => {
  if (!themeName) return 'default'

  return themeName
    .toLowerCase()
    .replace(/\s+/g, '-')           // Replace spaces with hyphens
    .replace(/[^a-z0-9\-]/g, '')   // Remove special characters except hyphens
    .replace(/\-+/g, '-')          // Replace multiple hyphens with single hyphen
    .replace(/^\-|\-$/g, '')       // Remove leading/trailing hyphens
}

/**
 * Get active theme for specific context from API
 * @param {string} context - Context ('admin' or 'site')
 * @returns {Promise<Object|null>} Active theme for context or null
 */
const getActiveThemeForContextFromAPI = async (context = 'site') => {
  const cacheKey = createCacheKey('getActiveThemeForContextFromAPI', { context })

  return withCache(cacheKey, async () => {
    try {
      // Get all themes and check which ones are active for this context
      const response = await GetCMSContent({
        match: {
          "content.type": "SiteTheme",
          deleted: { $ne: true }
        }
      })

      if (response.data && response.data.length > 0) {
        // Find a theme that's active for the specified context
        for (const themeData of response.data) {
          const theme = themeData.content
          const normalizedIsActive = normalizeIsActive(theme.isActive)
          const isActiveForContext = normalizedIsActive.includes(context)

          if (isActiveForContext) {
            return {
              ...theme,
              _id: themeData._id
            }
          }
        }
      }

      return null
    } catch (error) {
      console.error(`Failed to load active ${context} theme from API:`, error)
      return null
    }
  })
}


/**
 * Get default theme from API (fallback)
 */
const getDefaultThemeFromAPI = async () => {
  const cacheKey = createCacheKey('getDefaultThemeFromAPI', {})

  return withCache(cacheKey, async () => {
    try {
      const response = await GetCMSContent({
        match: {
          "content.type": "SiteTheme",
          "content.isDefault": true,
          deleted: { $ne: true }
        },
        limit: 1
      })

      if (response.data && response.data.length > 0) {
        return {
          ...response.data[0].content,
          _id: response.data[0]._id
        }
      }

      return null
    } catch (error) {
      console.error('Failed to load default theme from API:', error)
      return null
    }
  })
}

/**
 * Get theme by ID from API
 */
const getThemeByIdFromAPI = async (themeId) => {
  const cacheKey = createCacheKey('getThemeByIdFromAPI', { themeId })

  return withCache(cacheKey, async () => {
    try {
      // Handle built-in theme specially - it's not a MongoDB ObjectId
      if (themeId === 'built-in') {
        // Look up built-in theme by themeID field instead of _id
        const response = await GetCMSContent({
          match: {
            "content.type": "SiteTheme",
            "content.themeID": "built-in",
            deleted: { $ne: true }
          },
          limit: 1
        })

        if (response.data && response.data.length > 0) {
          return {
            ...response.data[0].content,
            _id: response.data[0]._id
          }
        }

        // If no built-in theme found in DB, return a minimal built-in theme object
        return {
          _id: 'built-in',
          name: 'Built-in',
          description: 'Default theme that is part of the site source code',
          builtIn: true,
          themeID: 'built-in',
          type: 'SiteTheme'
        }
      }

      // Handle regular MongoDB ObjectId themes
      const response = await GetCMSContent({
        match: {
          _id: themeId,
          "content.type": "SiteTheme",
          deleted: { $ne: true }
        },
        limit: 1
      })

      if (response.data && response.data.length > 0) {
        return {
          ...response.data[0].content,
          _id: response.data[0]._id
        }
      }

      return null
    } catch (error) {
      console.error('Failed to load theme by ID from API:', error)
      return null
    }
  })
}

/**
 * Deactivate all themes in API
 */
const deactivateAllThemes = async () => {
  try {
    const response = await GetCMSContent({
      match: {
        "content.type": "SiteTheme",
        "content.isActive": true,
        deleted: { $ne: true }
      }
    })

    if (response.data && response.data.length > 0) {
      for (const theme of response.data) {
        const updatedContent = {
          ...theme.content,
          isActive: false
        }

        await SaveCMSContent({
          payload: {
            ...theme,
            content: updatedContent
          }
        })
      }
    }
  } catch (error) {
    console.error('Failed to deactivate themes:', error)
  }
}

/**
 * Activate theme in API
 */
const activateThemeInAPI = async (themeId) => {
  try {
    const response = await GetCMSContent({
      match: {
        _id: themeId,
        "content.type": "SiteTheme",
        deleted: { $ne: true }
      },
      limit: 1
    })

    if (response.data && response.data.length > 0) {
      const theme = response.data[0]
      const updatedContent = {
        ...theme.content,
        isActive: true
      }

      await SaveCMSContent({
        payload: {
          ...theme,
          content: updatedContent
        }
      })
    }
  } catch (error) {
    console.error('Failed to activate theme:', error)
    throw error
  }
}

/**
 * Add theme change listener
 */
const onThemeChange = (callback) => {
  if (typeof callback === 'function') {
    onThemeChangeCallbacks.push(callback)
  }
}

/**
 * Remove theme change listener
 */
const offThemeChange = (callback) => {
  const index = onThemeChangeCallbacks.indexOf(callback)
  if (index > -1) {
    onThemeChangeCallbacks.splice(index, 1)
  }
}

/**
 * Notify theme change listeners
 */
const notifyThemeChange = (theme) => {
  onThemeChangeCallbacks.forEach(callback => {
    try {
      callback(theme)
    } catch (error) {
      console.error('Theme change callback error:', error)
    }
  })
}

/**
 * Create theme initialization script for SSR Head injection
 * This script applies themes immediately to prevent FOUC
 * @param {Object} themeData - SSR theme data containing activeTheme and availableThemes
 * @returns {string} JavaScript code as string for script tag
 */
const createThemeInitScript = (themeData) => {
  const themeDataJson = JSON.stringify(themeData || null);

  return `
    (function() {
      // Initialize SiteTheme system for immediate theme application
      // This prevents flash of unstyled content (FOUC)

      // SSR theme data
      const ssrThemeData = ${themeDataJson};

      // We'll regenerate CSS on the client if user preference differs from SSR theme
      // But for now, let's just detect the mismatch and log it
      const regenerateCSS = false; // Disabled for now - SSR should handle this

      try {
        // Helper function to normalize isActive field to array format
        function normalizeIsActive(isActive) {
          if (Array.isArray(isActive)) return isActive;
          if (isActive === true) return ['site']; // Legacy boolean true = site active
          if (isActive === false) return []; // Legacy boolean false = inactive
          return []; // Any other value = inactive
        }

        // Helper function to generate theme identifier
        function generateThemeIdentifier(themeName) {
          return (themeName || 'default')
            .toLowerCase()
            .replace(/\\s+/g, '-')
            .replace(/[^a-z0-9\\-]/g, '')
            .replace(/\\-+/g, '-')
            .replace(/^-|-$/g, '');
        }

        // Helper function to apply theme to DOM
        function applyTheme(theme, context) {
          if (!theme) {
            document.documentElement.setAttribute('data-theme', 'default');
            return;
          }

          const themeIdentifier = generateThemeIdentifier(theme.name);
          document.documentElement.setAttribute('data-theme', themeIdentifier);
          document.documentElement.setAttribute('data-theme-name', theme.name || 'Unknown');
          document.documentElement.setAttribute('data-theme-id', theme._id || 'unknown');
          document.documentElement.setAttribute('data-theme-version', theme.version || '1.0.0');
          document.documentElement.setAttribute('data-theme-context', context || 'site');

          // Default theme relies on Layout.css :root variables. Skip inline color
          // application so code-level CSS changes are not overridden.
          if (theme.isDefault) return;

          // Theme application is complete - CSS generation handled separately by admin components

          // Apply CSS variables from theme colors
          if (theme.colors) {
            const root = document.documentElement;
            // Clear existing theme variables
            const existingVars = [];
            for (let i = 0; i < root.style.length; i++) {
              const property = root.style.item(i);
              if (property.startsWith('--color-')) {
                existingVars.push(property);
              }
            }
            existingVars.forEach(prop => root.style.removeProperty(prop));

            // Apply new theme variables
            const applyColor = function(key, value) {
                if (!key || !value) return;
                const cssVarName = '--color-' + key.replace(/_/g, '_');
                root.style.setProperty(cssVarName, value);
            };

            if (Array.isArray(theme.colors)) {
                theme.colors.forEach(function(c) { applyColor(c.key, c.value) });
            } else if (typeof theme.colors === 'object') {
                Object.keys(theme.colors).forEach(function(k) { applyColor(k, theme.colors[k]) });
            }
          }
        }


        // Priority-based theme loading
        let selectedTheme = null;

        // Determine current context (admin or site)
        // Force site context for theme previews
        const urlParams = new URLSearchParams(window.location.search);
        const themePreviewId = urlParams.get('theme_preview');
        const currentContext = themePreviewId ? 'site' : (window.location.pathname.startsWith('/admin') ? 'admin' : 'site');


        // Skip theme preview handling in SSR script - let main initialization handle it properly
        // This prevents conflicts between SSR and main theme application logic

        // 1. Check LocalUserPreferences for user-selected theme (context-specific)
        try {
          const userPrefs = localStorage.getItem('local-user-preferences');

          if (userPrefs) {
            const prefs = JSON.parse(userPrefs);

            // Try context-specific preference first
            let userThemeId = currentContext === 'admin'
              ? prefs?.ui?.theme?.admin?.selectedId
              : prefs?.ui?.theme?.site?.selectedId;

            // Fall back to legacy preference if context-specific not found
            if (!userThemeId) {
              userThemeId = prefs?.ui?.theme?.selectedId;
            }

            if (userThemeId && ssrThemeData?.availableThemes) {
              selectedTheme = ssrThemeData.availableThemes.find(t => t._id === userThemeId);
            }
          }
        } catch (error) {
          console.warn('Failed to load user preferences:', error);
        }

        // 2. Fall back to SSR active theme (context-specific)
        if (!selectedTheme && ssrThemeData) {

          const activeThemeForContext = currentContext === 'admin'
            ? ssrThemeData.adminActiveTheme
            : ssrThemeData.siteActiveTheme;

          // Fall back to legacy activeTheme if context-specific not available
          const activeTheme = activeThemeForContext || ssrThemeData.activeTheme;


          if (activeTheme) {
            // Double-check that the theme is active for this context
            const isActiveForContext = normalizeIsActive(activeTheme.isActive).includes(currentContext);

            if (isActiveForContext || activeThemeForContext) { // If we got it from context-specific data, trust it
              selectedTheme = activeTheme;
            }
          }
        }

        // 3. Apply the selected theme with context
        applyTheme(selectedTheme, currentContext);

        // Store theme data globally for SiteTheme API
        window.__INITIAL_THEME_DATA__ = ssrThemeData;

      } catch (error) {
        console.warn('Theme initialization failed, using default:', error);
        document.documentElement.setAttribute('data-theme', 'default');
      }
    })();
  `;
}

/**
 * Get available themes for theme switchers
 * Uses SSR data when available for better performance
 */
const getAvailableThemes = async () => {
  try {
    // Try to use SSR data first
    const ssrThemeData = typeof window !== 'undefined' ? window.__INITIAL_THEME_DATA__ : null
    if (ssrThemeData?.availableThemes) {
      return ssrThemeData.availableThemes
    }

    // Fall back to API
    const response = await GetCMSContent({
      match: {
        "content.type": "SiteTheme",
        deleted: { $ne: true }
      },
      sort: { "content.isDefault": -1, createdAt: -1 },
      // Add cache buster to force fresh data
      _cacheBuster: Date.now()
    })

    if (response.data) {
      return response.data.map(theme => ({
        ...theme.content,
        _id: theme._id
      }))
    }

    return []
  } catch (error) {
    console.error('Failed to load available themes:', error)
    return []
  }
}

/**
 * Generate and inject CMS color utilities for a theme
 * This function calls the site-theme API to generate Tailwind utility classes
 * and injects them into the page for immediate use
 */
const generateCMSColorUtilities = async (theme) => {
  if (typeof window === 'undefined') return
  if (!theme) {
    console.warn('SiteTheme: Cannot generate CMS colors - no theme provided')
    return
  }

  const themeId = theme._id || theme.id
  if (!themeId) {
    console.warn('SiteTheme: Cannot generate CMS colors - no theme ID provided', theme)
    return
  }

  // The "Site Default" theme (isDefault: true) stores colors as top-level properties in an old
  // schema the CMS editor doesn't recognize. The server generates CSS with those values
  // (e.g. primary: "0 170 193") which overrides Tailwind's CSS variable classes.
  // Skip CMS stylesheet injection for the default theme so Layout.css CSS variables take over.
  if (theme.isDefault) {
    const cssElement = document.getElementById('cms-color-utilities')
    if (cssElement) cssElement.innerHTML = ''
    return
  }

  try {
    // Check if CSS already exists from SSR or previous client-side generation
    let cssElement = document.getElementById('cms-color-utilities')
    const existingCSS = cssElement ? cssElement.innerHTML.trim() : null
    const existingThemeId = cssElement?.dataset?.themeId || cssElement?.getAttribute('data-theme-id')

    // If we already have CSS for this theme, don't make another request
    if (existingCSS && existingThemeId === themeId) {
      return
    }

    // Check if we're in a theme preview
    const urlParams = new URLSearchParams(window.location.search)
    const isThemePreview = urlParams.get('theme_preview')

    // For theme previews, trust SSR CSS if it exists and matches the theme
    if (isThemePreview && existingCSS) {
      // If we have existing CSS but no theme ID marker, assume it's for the current theme
      if (!existingThemeId) {
        cssElement.setAttribute('data-theme-id', themeId)
      }
      return
    }

    // For regular pages with SSR CSS, trust it only if it's already for this theme
    // (prevents flash on first load without blocking theme switches)
    if (existingCSS && existingThemeId === themeId) {
      return
    }

    // Call the site-theme API to generate CSS
    const response = await siteThemeAPI({
      get_cms_color_stylesheet: {
        themeId: themeId
      }
    })

    if (response.result && response.result.css && response.result.css.trim()) {
      // Strip admin_* color rules — admin colors are defined by CSS variables in Layout.css
      // and must not be overridden by the site theme stylesheet
      const filteredCSS = response.result.css.replace(
        /[^{}]*\badmin_\w+\b[^{}]*\{[^}]*\}/g,
        ''
      )

      // Find or create the CSS style element
      if (!cssElement) {
        cssElement = document.createElement('style')
        cssElement.id = 'cms-color-utilities'
        cssElement.type = 'text/css'
        document.head.appendChild(cssElement)
      }

      // Update CSS and mark with theme ID
      cssElement.innerHTML = filteredCSS
      cssElement.dataset.themeId = themeId

      // Force DOM refresh to ensure styles are applied immediately
      const parent = cssElement.parentNode
      if (parent) {
        parent.removeChild(cssElement)
        parent.appendChild(cssElement)
      }
    } else if (existingCSS) {
      // Keep existing CSS if API didn't return valid CSS
      console.warn('SiteTheme: API returned empty CSS, keeping existing CSS')
    }
  } catch (error) {
    console.warn('SiteTheme: Failed to generate CMS color utilities, keeping existing CSS:', error)
  }
}

// Auto-initialize on client-side
if (typeof window !== 'undefined') {
  // Set up global SiteTheme object
  window.SiteTheme = {
    initialize,
    getTheme,
    setTheme,
    setSiteActiveTheme,
    getUserPreferredThemeId,
    clearUserPreference,
    getAvailableThemes,
    applyThemeToDOM,
    onThemeChange,
    offThemeChange
  }

  // Auto-initialize when DOM is ready
  // Add message listener for theme preview updates
  if (typeof window !== 'undefined') {
    window.addEventListener('message', async (event) => {
      // Only accept messages from same origin for security
      if (event.origin !== window.location.origin) return

      if (event.data.type === 'THEME_PREVIEW_CHANGE' && event.data.theme) {
        // Apply the theme directly without changing preferences
        const previewTheme = event.data.theme
        currentTheme = previewTheme

        // Apply full theme via applyThemeToDOM (including CMS colors)
        if (previewTheme._id || previewTheme.id) {
          // Theme has ID - use full applyThemeToDOM for CMS colors
          await applyThemeToDOM(previewTheme, 'site')
        } else {
          // Theme missing ID - apply only CSS variables
          if (typeof window !== 'undefined') {
            const root = document.documentElement

            // Apply CSS variables from theme colors
            if (previewTheme.colors && typeof previewTheme.colors === 'object') {
              Object.entries(previewTheme.colors).forEach(([colorKey, colorValue]) => {
                const cssVarName = `--color-${colorKey.replace(/_/g, '_')}`
                if (colorValue && cssVarName) {
                  root.style.setProperty(cssVarName, colorValue)
                }
              })
            }

            // Set theme attributes
            const themeIdentifier = generateThemeIdentifier(previewTheme.name)
            root.setAttribute('data-theme', themeIdentifier)
            root.setAttribute('data-theme-name', previewTheme.name || 'Unknown')
          }
        }

        notifyThemeChange(previewTheme)
      }
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize)
  } else {
    // DOM is already ready
    setTimeout(initialize, 0)
  }
}

// Export for module usage
export default {
  initialize,
  getTheme,
  setTheme,
  setSiteActiveTheme,
  getUserPreferredThemeId,
  clearUserPreference,
  getAvailableThemes,
  createThemeInitScript,
  onThemeChange,
  offThemeChange,
  applyThemeToDOM,
  generateThemeIdentifier,
  getThemeById: getThemeByIdFromAPI
}

export {
  initialize,
  getTheme,
  setTheme,
  setSiteActiveTheme,
  getUserPreferredThemeId,
  clearUserPreference,
  getAvailableThemes,
  createThemeInitScript,
  onThemeChange,
  offThemeChange,
  getThemeByIdFromAPI as getThemeById
}

// Framework integration helpers
export { loadThemeDataForSSR, generateCMSColorCSS, createThemeHeadElements } from './vike-helpers.js'
export { ThemeProvider as FrameworkThemeProvider, ThemeHead, useTheme as useFrameworkTheme } from './react-helpers.jsx'